import { BehaviorSubject, from, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { first } from 'rxjs/internal/operators/first';
import { map, mergeMap, takeLast, takeUntil, tap } from 'rxjs/operators';
import { HdWizardController } from './hd-wizard-controller';
import { HdWizardQueryParamHandler } from './hd-wizard-query-param-handler';
import { HdWizardStepController } from './hd-wizard-step-controller';
import { HdWizardState, HdWizardStateWrapper, HdWizardStepMeta, HdWizardTimeTravelData, WizardVersion } from './interface';
import { HdWizardStateMachineInterface } from './state-machine';
import { HdWizardStepTracker } from './step-tracker';
import { wrapIntoObservable } from './utils';

export class HdWizard {
  stepBackSub: Subscription = Subscription.EMPTY;
  stepCompleteSub: Subscription = Subscription.EMPTY;
  stepTitleSub: Subscription = Subscription.EMPTY;
  stepSkipSub: Subscription = Subscription.EMPTY;
  stepDirtySub: Subscription = Subscription.EMPTY;

  /**
   * Flag that decides whether the current step will be skipped and
   * removed from step tracker when the next step is resolved.
   */
  private _skipCurrentStep = false;

  private _handleQueryParams = true;
  private _queryParamsChangesListener: any;
  private _destroyed$ = new Subject<void>();
  private _unloadListener;

  private _wizardStateSubject = new BehaviorSubject<HdWizardStateWrapper>({ state: HdWizardState.INIT });
  private _currentStepSubject = new BehaviorSubject<HdWizardCurrentStep>(null);

  get currentStep() {
    return this._currentStepSubject.getValue();
  }

  set currentStep(step) {
    this._currentStepSubject.next(step);
  }

  currentStep$ = this._currentStepSubject.asObservable();
  wizardState$ = this._wizardStateSubject.asObservable();

  constructor(
    private _controller: HdWizardController,
    private _stateMachine: HdWizardStateMachineInterface,
    public stepTracker: HdWizardStepTracker,
    private _queryParamHandler: HdWizardQueryParamHandler
  ) {
    if (this._handleQueryParams) {
      this._subscribeQueryParams();
    } else {
      this._goToNextStep();
    }

    this._unloadListener = () => {
      this.saveStepBeforeUnload();
    };

    window.addEventListener('beforeunload', this._unloadListener);
  }

  /**
   * Query param handling
   */
  private _getStepPathFromQueryParam(): string {
    return this._queryParamHandler.getPath(this._stateMachine.queryParamKey);
  }

  private _updateQueryParam(path: string) {
    const currentStepIndex = this.stepTracker.currentStepIndex;
    const replaceUrl = currentStepIndex === -1 || !this._getStepPathFromQueryParam() || this._skipCurrentStep;

    this._queryParamHandler.updatePath(
      this._stateMachine.queryParamKey,
      path,
      replaceUrl
    );
  }

  private _subscribeQueryParams() {
    this._queryParamsChangesListener = this._queryParamHandler.changes(
      this._stateMachine.queryParamKey,
      () => {
        const intendedPath: string = this._getStepPathFromQueryParam();
        const intendedStep = this._stateMachine.getStepFromPath(intendedPath);

        if (!intendedStep) {
          this._resetAndGotoInitialStep();
        } else {
          this._goToStep(intendedStep);
        }
      }
    );
  }

  /**
   * Next step path is returned as an observable, so we wait for the observable
   * to resolve and meanwhile show loading component if specified by state machine
   */
  private _resolveStepPath(pathResolver: string | Observable<string> | Promise<string>, skipUpdateQueryParams: boolean = false) {
    this._wizardStateSubject.next({ state: HdWizardState.LOADING_STEP });

    wrapIntoObservable(pathResolver).subscribe((path: string) => {
      if (!path) {
        this._markWizardAsFinished();
        return;
      }

      if (!skipUpdateQueryParams && this._handleQueryParams) {
        this._updateQueryParam(path);
      } else {
        this._resolveStep(path);
      }
    }, (error) => {
      this._wizardStateSubject.next({ state: HdWizardState.ERRORED, error });
    });
  }

  private _resolveStep(path: string) {
    this._selectStep(this._stateMachine.getStepFromPath(path));
  }

  private _selectStep(stepMeta: HdWizardStepMeta) {
    this.runResolvers(stepMeta).subscribe((data: any) => {
      /**
       * If next step is final or undefined, notify wizard about completion
       */
      if (stepMeta.isFinal) {
        this._controller.markAsComplete();
      }
      /**
       * Track next step
       */
      this.stepTracker.track(stepMeta, this._skipCurrentStep);
      this._skipCurrentStep = false;
      this.stepTracker.saveStepParams(stepMeta);

      const stepData = this.stepTracker.getStepData(stepMeta.path);
      const controller: HdWizardStepController = new HdWizardStepController(
        this,
        stepMeta.params || {},
        data,
        stepData,
        stepMeta.isFinal,
        this.stepTracker.getStepIndexFromPath(stepMeta.path) === 0
      );

      this._wizardStateSubject.next({ state: HdWizardState.IDLE });

      let component;

      if (typeof stepMeta.resolveComponent === 'function') {
        component = stepMeta.resolveComponent();
      } else {
        component = stepMeta.component;
      }

      this.currentStep = {
        component,
        controller,
        path: stepMeta.path
      };

      controller.unsavedData = stepData;

      this.subscribeStepActions();
    }, (error: any) => {
      this._wizardStateSubject.next({ state: HdWizardState.ERRORED, error });
    });
  }


  /**
   * Returns an observable that resolves step data. While
   * resolving data a loading component can be shown if provided
   * by the state machine
   */
  runResolvers(stepMeta: HdWizardStepMeta): Observable<any> {
    const resolveData = stepMeta.resolve || {};
    const keys: string[] = Object.keys(resolveData);

    if (!keys.length) {
      return of({});
    }

    const data: {[k: string]: any} = {};

    return from(keys).pipe(
      mergeMap((key: string) => {
        return wrapIntoObservable(resolveData[key].resolve
          ? resolveData[key].resolve(this.stepTracker, stepMeta.params)
          : resolveData[key]
        ).pipe(
          first(),
          tap(value => {
            data[key] = value;
          })
        );
      }),
      map(() => {
        return data;
      }),
      takeLast(1)
    );
  }

  /**
   * Handle step complete and back actions
   */
  subscribeStepActions() {
    this._clearStepActionsSub();
    if (!this.currentStep) {
      return;
    }
    this.stepBackSub = merge(
      this.currentStep.controller.back,
      this._controller.requestBack
    ).subscribe((path) => {
      this._onStepBack(path);
    });

    this.stepCompleteSub = this.currentStep.controller.complete.subscribe(() => {
      this._onStepComplete();
    });

    this.stepTitleSub = this.currentStep.controller.title.subscribe((data: string) => {
      this._onStepTitleChange(data);
    });

    this.stepSkipSub = this.currentStep.controller.skip.subscribe(() => {
      this._onStepSkip();
    });

    this.stepDirtySub = this.currentStep.controller.dirty.subscribe(() => {
      this.stepTracker.markCurrentStepDirty(this.currentStep.path);
    });
  }

  private _clearStepActionsSub() {
    this.stepBackSub.unsubscribe();
    this.stepCompleteSub.unsubscribe();
    this.stepTitleSub.unsubscribe();
    this.stepDirtySub.unsubscribe();
  }

  private _onStepComplete() {
    /**
     * Save current step data in step tracker
     */
    this.stepTracker.saveStepData(
      this.currentStep.path,
      this.currentStep.controller.unsavedData
    );
    this.stepTracker.markStepAsComplete(this.currentStep.path);
    this._goToNextStep();
  }

  private _goToStep(step: HdWizardStepMeta) {
    /**
     * Bypass step validation for old versions
     */
    if (this.stepTracker.version !== WizardVersion.V2) {
      this._selectStep(step);
      return;
    }

    const stepIndex = this.stepTracker.getStepIndexFromPath(step.path);

    const existingSteps = this.stepTracker.getSteps();

    /**
     * if the step already exists in the stack, it can be safely visited.
     */
    if (stepIndex >= 0) {
      this._selectStep(step);
      return;
    }

    /**
     * If an existing step's next step is same as the requested step, it can be safely visited
     */
    if (existingSteps.length && existingSteps.find((existingStep) => existingStep.next === step.path)) {
      this._selectStep(step);
      return;
    }

    /**
     * As a last resort if the requested step is same as initial step, allow it to be visited
     * without resetting the wizard, else reset the wizard and go to initial step.
     */
    wrapIntoObservable(this._stateMachine.getInitialPath(this.stepTracker)).pipe(
      takeUntil(this._destroyed$)
    ).subscribe((initialPath) => {
      if (step.path !== initialPath) {
        /**
         * Avoid fetching initial path again, as it was just resolved
         */
        this._resetAndGotoInitialStep(initialPath);
      } else {
        this._selectStep(step);
      }
    });
  }

  private _goToNextStep() {
    let pathResolver: string | Observable<string> | Promise<string>;
    if (this.stepTracker.currentStep) {
      const currentStepRef = this._stateMachine.getStepFromPath(this.stepTracker.currentStep.path);
      pathResolver = currentStepRef.nextStep
        ? currentStepRef.nextStep
        : currentStepRef.getNextStep(
          this.stepTracker,
          currentStepRef,
          this._skipCurrentStep
        );

      /**
       * Mark the next applicable step before proceeding, to later check the validity of the step that user is trying
       * to access
       */
      pathResolver = wrapIntoObservable(pathResolver).pipe(
        tap((path) => {
          this.stepTracker.currentStep.next = path;
        })
      );

    } else {
      pathResolver = this._stateMachine.getInitialPath(this.stepTracker);
    }

    if (!pathResolver) {
      this._markWizardAsFinished();
      return;
    }

    this._resolveStepPath(pathResolver);
  }

  private _onStepBack(path: string) {
    this.saveStepBeforeUnload();

    if (this._handleQueryParams) {
      this._queryParamHandler.goBack(path);
      return;
    }

    const stepTrackerStep = path
      ? this.stepTracker.getStepRefFromPath(path)
      : this.stepTracker.getPrevStep(this.currentStep.path);

    if (stepTrackerStep) {
      this._resolveStepPath(stepTrackerStep.path);
    }
  }

  private _onStepSkip() {
    this._skipCurrentStep = true;
    this._goToNextStep();
  }

  private _onStepTitleChange(title: string) {
    this.stepTracker.updateStepTitle(this.currentStep.path, title);
  }

  private _markWizardAsFinished() {
    this._controller.markAsComplete();
    this.stepTracker.markAsComplete();
  }

  private _resetAndGotoInitialStep(initialPath?: string) {
    this.currentStep = undefined;
    this.stepTracker.reset();

    /**
     * If the initial path is provided as input, no need to fetch it again
     */
    if (initialPath) {
      this._resolveStepPath(initialPath);
    } else {
      this._goToNextStep();
    }
  }


  destroy() {
    this._clearStepActionsSub();
    this._destroyed$.next();
    this._destroyed$.complete();

    if (typeof this._queryParamsChangesListener === 'function') {
      this._queryParamsChangesListener();
    }

    if (this._unloadListener) {
      window.removeEventListener('beforeunload', this._unloadListener);
    }
  }

  saveStepBeforeUnload() {
    if (typeof this.currentStep.beforeUnload === 'function') {
      this.currentStep.beforeUnload();
    }
    this.stepTracker.saveStepData(this.currentStep.path, this.currentStep.controller.unsavedData);
  }

  goTo(path: string) {
    this.saveStepBeforeUnload();
    this._resolveStepPath(path);
  }

  fastTravel(data: HdWizardTimeTravelData) {
    this.stepTracker.fastTravel(data);

    if (!data.steps || !data.steps.length) {
      this._goToNextStep();
      return;
    }

    const path = data.steps[data.steps.length - 1].path;

    if (data.next && data.steps[data.steps.length - 1].complete) {
      this._goToNextStep();
    } else {
      this._resolveStepPath(path, !!data?.skipUpdateQueryParams);
    }
  }

  closeWizard() {
    this.currentStep?.controller.setCloseWizard();
  }

  getController() {
    return this._controller;
  }
}

export interface HdWizardCurrentStep {
  component?: any;
  beforeUnload?: Function;
  path: string;
  controller: HdWizardStepController;
}
